const ARC_START = 0.75 * Math.PI; // 135 degrees
const ARC_END = 2.25 * Math.PI; // 405 degrees
const ARC_RANGE = ARC_END - ARC_START;
const RADIUS = 17;
const CENTER = 22;

function polarToCart(angle, r) {
  return {
    x: CENTER + r * Math.cos(angle),
    y: CENTER + r * Math.sin(angle),
  };
}

function describeArc(startAngle, endAngle, r) {
  const start = polarToCart(endAngle, r);
  const end = polarToCart(startAngle, r);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function arcLength(startAngle, endAngle, r) {
  return Math.abs(endAngle - startAngle) * r;
}

export function createKnob({
  state,
  path,
  label,
  min = 0,
  max = 1,
  step = 0.01,
  log = false,
  displayFn = null,
}) {
  const container = document.createElement('div');
  container.className = 'panel-control';

  const knobDiv = document.createElement('div');
  knobDiv.className = 'knob-container';

  // SVG
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 44 44');

  // Track arc
  const trackPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  trackPath.setAttribute('d', describeArc(ARC_START, ARC_END, RADIUS));
  trackPath.setAttribute('class', 'knob-track');
  const totalLen = arcLength(ARC_START, ARC_END, RADIUS);
  trackPath.setAttribute('stroke-dasharray', `${totalLen}`);

  // Value arc
  const valuePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  valuePath.setAttribute('d', describeArc(ARC_START, ARC_END, RADIUS));
  valuePath.setAttribute('class', 'knob-value');
  valuePath.setAttribute('stroke-dasharray', `${totalLen}`);

  // Indicator dot
  const indicator = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  indicator.setAttribute('r', '2.5');
  indicator.setAttribute('class', 'knob-indicator');

  svg.appendChild(trackPath);
  svg.appendChild(valuePath);
  svg.appendChild(indicator);
  knobDiv.appendChild(svg);

  // Value label
  const valueLabel = document.createElement('div');
  valueLabel.className = 'knob-value-label';

  // Label
  const labelEl = document.createElement('label');
  labelEl.textContent = label;

  container.appendChild(knobDiv);
  container.appendChild(valueLabel);
  container.appendChild(labelEl);

  // Normalize / denormalize
  function normalize(value) {
    if (log) {
      const minLog = Math.log(Math.max(min, 0.001));
      const maxLog = Math.log(max);
      return (Math.log(Math.max(value, 0.001)) - minLog) / (maxLog - minLog);
    }
    return (value - min) / (max - min);
  }

  function denormalize(norm) {
    if (log) {
      const minLog = Math.log(Math.max(min, 0.001));
      const maxLog = Math.log(max);
      return Math.exp(minLog + norm * (maxLog - minLog));
    }
    return min + norm * (max - min);
  }

  function quantize(value) {
    return Math.round(value / step) * step;
  }

  function update(value) {
    const norm = Math.max(0, Math.min(1, normalize(value)));
    const angle = ARC_START + norm * ARC_RANGE;

    // Update value arc: show from start to current value
    const visibleLen = arcLength(ARC_START, angle, RADIUS);
    const offset = totalLen - visibleLen;
    valuePath.setAttribute('stroke-dashoffset', `${offset}`);

    // Update indicator position
    const pos = polarToCart(angle, RADIUS);
    indicator.setAttribute('cx', pos.x);
    indicator.setAttribute('cy', pos.y);

    // Update value label
    if (displayFn) {
      valueLabel.textContent = displayFn(value);
    } else if (max >= 100) {
      valueLabel.textContent = Math.round(value);
    } else {
      valueLabel.textContent = value.toFixed(2);
    }
  }

  // Pointer interaction
  let dragging = false;
  let startY = 0;
  let startValue = 0;

  knobDiv.addEventListener('pointerdown', (e) => {
    dragging = true;
    startY = e.clientY;
    startValue = normalize(state.get(path));
    knobDiv.setPointerCapture(e.pointerId);
    e.preventDefault();
  });

  knobDiv.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dy = startY - e.clientY;
    const sensitivity = e.shiftKey ? 0.001 : 0.005;
    const newNorm = Math.max(0, Math.min(1, startValue + dy * sensitivity));
    const newValue = quantize(denormalize(newNorm));
    state.set(path, newValue);
  });

  knobDiv.addEventListener('pointerup', () => {
    dragging = false;
  });

  // Double-click to reset to default center
  knobDiv.addEventListener('dblclick', () => {
    const defaultVal = min + (max - min) * 0.5;
    state.set(path, quantize(defaultVal));
  });

  // Subscribe to state
  state.subscribe(path, (value) => {
    update(value);
  });

  return container;
}
